/*
 * Nola Wishlist — localStorage-backed favorites store.
 *
 * Storage shape (key: nola:wishlist):
 *   { [handle]: { handle, title, url, image, price, compareAtPrice, addedAt } }
 *
 * Legacy shape (array of handles) is migrated on first read.
 *
 * Public API: window.NolaWishlist
 *   .has(handle)            -> boolean
 *   .toggle(handle, snap)   -> boolean (true if now in list)
 *   .add(handle, snap)
 *   .remove(handle)
 *   .list()                 -> snapshot[] (newest first)
 *   .count()                -> number
 *   .on(event, cb)          -> unsubscribe fn
 *
 * Events (on document & via .on()):
 *   'nola:wishlist:updated' -> detail: { count, items }
 */
(function () {
  if (window.NolaWishlist) return;

  const KEY = 'nola:wishlist';

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const obj = {};
        for (const h of parsed) {
          if (typeof h === 'string') obj[h] = { handle: h, addedAt: Date.now() };
        }
        write(obj);
        return obj;
      }
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  };

  const write = (obj) => {
    try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch {}
  };

  let state = read();
  const listeners = new Set();

  const emit = () => {
    const detail = { count: count(), items: list() };
    document.dispatchEvent(new CustomEvent('nola:wishlist:updated', { detail }));
    listeners.forEach((cb) => { try { cb(detail); } catch {} });
  };

  const has = (handle) => Boolean(handle && state[handle]);

  const list = () =>
    Object.values(state).sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));

  const count = () => Object.keys(state).length;

  const add = (handle, snap = {}) => {
    if (!handle) return;
    state[handle] = {
      handle,
      title: snap.title || state[handle]?.title || handle,
      url: snap.url || state[handle]?.url || `/products/${handle}`,
      image: snap.image || state[handle]?.image || '',
      price: snap.price || state[handle]?.price || '',
      compareAtPrice: snap.compareAtPrice || state[handle]?.compareAtPrice || '',
      addedAt: state[handle]?.addedAt || Date.now(),
    };
    write(state);
    emit();
  };

  const remove = (handle) => {
    if (!handle || !state[handle]) return;
    delete state[handle];
    write(state);
    emit();
  };

  const toggle = (handle, snap = {}) => {
    if (has(handle)) { remove(handle); return false; }
    add(handle, snap);
    return true;
  };

  const on = (event, cb) => {
    if (event !== 'nola:wishlist:updated') return () => {};
    listeners.add(cb);
    return () => listeners.delete(cb);
  };

  // Cross-tab sync
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    state = read();
    syncAllButtons();
    syncBadges();
    renderDrawer();
    listeners.forEach((cb) => { try { cb({ count: count(), items: list() }); } catch {} });
  });

  // Sync exposed below after syncAllButtons is defined.
  window.NolaWishlist = { has, add, remove, toggle, list, count, on };

  /* ------------------------------------------------------------------ */
  /* DOM behaviour                                                      */
  /* ------------------------------------------------------------------ */

  const snapshotFromButton = (btn) => ({
    title: btn.dataset.productTitle || '',
    url: btn.dataset.productUrl || '',
    image: btn.dataset.productImage || '',
    price: btn.dataset.productPrice || '',
    compareAtPrice: btn.dataset.productComparePrice || '',
  });

  const syncButton = (btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const pressed = has(handle);
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    if (pressed) {
      btn.setAttribute('aria-label', btn.dataset.removeLabel || `Remove from favorites`);
    }
  };

  const syncAllButtons = (root = document) => {
    root.querySelectorAll('[data-nola-wishlist]').forEach(syncButton);
  };

  // Public method so consumers (e.g. load-more, AJAX swaps) can re-sync
  // newly inserted buttons without re-implementing storage logic.
  window.NolaWishlist.sync = syncAllButtons;

  const syncBadges = () => {
    const c = count();
    document.querySelectorAll('[data-nola-wishlist-count]').forEach((el) => {
      el.textContent = c;
      el.toggleAttribute('hidden', c === 0);
    });
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-nola-wishlist]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    toggle(handle, snapshotFromButton(btn));
    // Sync all matching buttons (PDP + card may both be on screen)
    document
      .querySelectorAll(`[data-nola-wishlist][data-product-handle="${CSS.escape(handle)}"]`)
      .forEach(syncButton);
  });

  /* ----------------------- Drawer rendering ------------------------- */

  const escapeHtml = (str) =>
    String(str || '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

  const renderDrawer = () => {
    const drawers = document.querySelectorAll('[data-nola-wishlist-drawer]');
    if (!drawers.length) return;
    const items = list();
    const c = items.length;

    const itemsHtml = items.length
      ? items.map((it) => {
          const img = it.image
            ? `<img src="${escapeHtml(it.image)}" alt="" loading="lazy" width="80" height="100" />`
            : `<div class="nola-wish__thumb-empty" aria-hidden="true"></div>`;
          const compare = it.compareAtPrice
            ? `<s class="nola-wish__compare">${escapeHtml(it.compareAtPrice)}</s>`
            : '';
          return `
            <li class="nola-wish__item" data-handle="${escapeHtml(it.handle)}">
              <a href="${escapeHtml(it.url)}" class="nola-wish__media" tabindex="-1" aria-hidden="true">${img}</a>
              <div class="nola-wish__meta">
                <a href="${escapeHtml(it.url)}" class="nola-wish__title-link">
                  <h3 class="nola-wish__title">${escapeHtml(it.title)}</h3>
                </a>
                <p class="nola-wish__price">
                  <span>${escapeHtml(it.price)}</span>
                  ${compare}
                </p>
              </div>
              <button type="button" class="nola-wish__remove"
                      data-nola-wishlist-remove
                      data-handle="${escapeHtml(it.handle)}"
                      aria-label="Remove ${escapeHtml(it.title)} from favorites">
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                </svg>
              </button>
            </li>`;
        }).join('')
      : '';

    drawers.forEach((drawer) => {
      const countEl = drawer.querySelector('[data-nola-wishlist-drawer-count]');
      if (countEl) countEl.textContent = c ? `(${c})` : '';

      const body = drawer.querySelector('[data-nola-wishlist-body]');
      if (!body) return;

      if (!items.length) {
        body.innerHTML = `
          <div class="nola-wish__empty">
            <p class="nola-wish__empty-title">Your favorites list is empty</p>
            <p class="nola-wish__empty-sub">Tap the heart on any product to save it for later.</p>
            <button type="button" class="nola-wish__empty-cta" data-nola-wishlist-close>Continue shopping</button>
          </div>`;
      } else {
        body.innerHTML = `<ul class="nola-wish__list" role="list">${itemsHtml}</ul>`;
      }
    });
  };

  /* ----------------------- Drawer open/close ------------------------ */

  const openDrawer = () => {
    const dlg = document.querySelector('[data-nola-wishlist-drawer]');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    renderDrawer();
    dlg.showModal();
  };

  const closeDrawer = () => {
    document.querySelectorAll('[data-nola-wishlist-drawer][open]').forEach((dlg) => dlg.close());
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nola-wishlist-toggle]')) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-nola-wishlist-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    const removeBtn = e.target.closest('[data-nola-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      remove(removeBtn.dataset.handle);
      return;
    }
  });

  // Click backdrop to close
  document.addEventListener('click', (e) => {
    const dlg = e.target.closest('dialog[data-nola-wishlist-drawer]');
    if (dlg && e.target === dlg) dlg.close();
  });

  // Re-render drawer body whenever state changes
  document.addEventListener('nola:wishlist:updated', () => {
    syncBadges();
    renderDrawer();
  });

  /* --------------------------- Boot --------------------------------- */

  const boot = () => {
    syncAllButtons();
    syncBadges();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
